// Chapters, as a strip of the video rather than a row of buttons.
//
// The old control was two controls: a scrolling rail of equal-width buttons
// with < > arrows on desktop, and a dropdown on mobile. Neither said anything
// true about the video -- every chapter looked the same width whether it ran
// twelve seconds or nine minutes, and neither showed how far into the current
// one you were.
//
// This is one control on every screen: segments proportional to their real
// length, the segment you are inside lit from its start to the playhead. The
// names live in a menu, because a name is worth reading when you are choosing
// one and worth nothing while you watch.

import React, { useMemo, useState } from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatTime } from '../ps/format';

// Chapter n runs until chapter n+1 starts, and the last one runs to the end.
// A duration of 0 means metadata has not landed yet, so the segments fall back
// to equal widths -- the strip still works as a jump control, it just cannot
// claim to be to scale.
function toSegments(chapters, duration) {
  const end = duration > 0 ? duration : 0;
  return chapters.map((chapter, index) => {
    const start = Math.max(0, Number(chapter.time) || 0);
    const next = chapters[index + 1];
    const stop = next ? Math.max(start, Number(next.time) || 0) : end || start + 1;
    return { ...chapter, start, length: end ? Math.max(stop - start, 0.001) : 1 };
  });
}

export const Chapters = React.memo(function Chapters({
  chapters,
  currentTime,
  duration = 0,
  onChapterClick,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const segments = useMemo(() => toSegments(chapters || [], duration), [chapters, duration]);

  const activeIndex = useMemo(() => {
    let found = -1;
    segments.forEach((segment, index) => {
      if (currentTime >= segment.start) found = index;
    });
    return found;
  }, [segments, currentTime]);

  if (!segments.length) return null;

  const active = segments[activeIndex];
  // How far through the current chapter the playhead sits, 0..1.
  const played = active ? Math.min(1, Math.max(0, (currentTime - active.start) / active.length)) : 0;

  const jump = (segment) => {
    setAnchorEl(null);
    onChapterClick(segment.start);
  };

  return (
    <>
      <div className="learn-chapters" role="group" aria-label="Chapters">
        {segments.map((segment, index) => (
          <Tooltip
            key={`${segment.start}-${index}`}
            title={`${segment.title}  ${formatTime(segment.start)}`}
            enterDelay={200}
          >
            <button
              type="button"
              aria-label={`Jump to ${segment.title}`}
              className={
                'learn-chapters__seg' +
                (index === activeIndex ? ' is-current' : '') +
                (index < activeIndex ? ' is-past' : '')
              }
              style={{ flexGrow: segment.length, '--seg': played }}
              onClick={() => jump(segment)}
            />
          </Tooltip>
        ))}
      </div>

      {/* The name of where you are, in the value voice, with the list one
          click away. Truncates rather than wraps: the bar keeps its height. */}
      <Box
        component="button"
        type="button"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          height: 22,
          maxWidth: { xs: 240, sm: 320 },
          px: '8px',
          border: '1px solid transparent',
          borderRadius: '5px',
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          cursor: 'pointer',
          transition: 'border-color 130ms cubic-bezier(0.22, 1, 0.36, 1)',
          '&:hover': { borderColor: 'var(--ps-line)' },
        }}
      >
        <span className="ps-label ps-label--dim" style={{ flexShrink: 0 }}>
          CH {activeIndex >= 0 ? activeIndex + 1 : 1}/{segments.length}
        </span>
        <span
          style={{
            fontSize: 12.5,
            color: 'var(--ps-gold)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {active ? active.title : segments[0].title}
        </span>
        <ExpandLessIcon sx={{ fontSize: 15, color: 'var(--ps-text-dim)', flexShrink: 0 }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 240 } } }}
      >
        {segments.map((segment, index) => (
          <MenuItem
            key={`${segment.start}-${index}`}
            selected={index === activeIndex}
            onClick={() => jump(segment)}
            sx={{ gap: '12px', justifyContent: 'space-between' }}
          >
            <span>{segment.title}</span>
            <span style={{ color: 'var(--ps-gold)', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
              {formatTime(segment.start)}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

export default Chapters;
